import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fmtMoney, monthLabel } from '../../utils/money';
import PaymentBadge from '../../components/financial/PaymentBadge';

const API = process.env.REACT_APP_API || '/api';

// بوابة العرض العامة للتقرير المالي — بلا تسجيل دخول، حسب المشاريع، المنشور فقط.
export default function FinancialPortal() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [month, setMonth] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);
  const [meta, setMeta] = useState(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchCover = useCallback(async (pwd, m) => {
    setLoading(true);
    setError('');
    try {
      const headers = {};
      if (pwd) headers['X-Financial-Password'] = pwd;
      const qs = m ? `?month=${m}` : '';
      const res = await fetch(`${API}/financial/portal/${slug}${qs}`, { headers });
      const json = await res.json();
      if (res.status === 401 && json.password_required) {
        setNeedsPassword(true);
        setMeta(json.funder);
        if (pwd) setError('كلمة المرور غير صحيحة');
        setData(null);
      } else if (!res.ok) {
        setError(json.error || 'تعذّر تحميل التقرير');
        setData(null);
      } else {
        setData(json);
        setMonth(json.period.report_month);
        setNeedsPassword(false);
      }
    } catch {
      setError('فشل تحميل الصفحة');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    const saved = sessionStorage.getItem(`fin-portal-pwd-${slug}`);
    fetchCover(saved || null, null);
  }, [fetchCover, slug]);

  const submitPassword = (e) => {
    e.preventDefault();
    sessionStorage.setItem(`fin-portal-pwd-${slug}`, password);
    fetchCover(password, null);
  };

  const switchMonth = (m) => {
    const pwd = sessionStorage.getItem(`fin-portal-pwd-${slug}`);
    fetchCover(pwd || null, m);
  };

  if (loading) return <div style={wrap}><div className="loader">جاري التحميل...</div></div>;

  if (needsPassword) {
    return (
      <div style={wrap}>
        <form onSubmit={submitPassword} className="card" style={{ maxWidth: 360, margin: '60px auto', textAlign: 'center' }}>
          {meta?.logo_path && <img src={meta.logo_path} alt="" style={{ maxHeight: 60, marginBottom: 12 }} />}
          <div className="card-title" style={{ marginBottom: 12 }}>{meta?.name || 'تقرير مالي'}</div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>هذه الصفحة محمية بكلمة مرور.</p>
          <input className="form-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="كلمة المرور" style={{ margin: '10px 0' }} />
          {error && <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 8 }}>{error}</div>}
          <button className="btn btn-primary" type="submit" style={{ width: '100%' }}>دخول</button>
        </form>
      </div>
    );
  }

  if (error) return <div style={wrap}><div className="empty">{error}</div></div>;
  if (!data) return <div style={wrap}><div className="empty">لا يوجد تقرير منشور</div></div>;

  const { funder, period, summary, months } = data;
  const projectLink = (id) => `/financial/${slug}/projects/${id}?month=${month}`;

  return (
    <div style={wrap} dir="rtl">
      <div className="report-cover" style={{ borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
        <div className="report-cover-inner" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
          <div className="report-cover-text">
            <div className="report-cover-kicker">تقرير مالي شهري</div>
            <h1 className="report-cover-title">{funder.name}</h1>
            <div className="report-cover-period">{monthLabel(period.report_month)}{period.letter_no ? ` · ${period.letter_no}` : ''}</div>
          </div>
          {funder.logo_path && <img src={funder.logo_path} alt={funder.name} style={{ maxHeight: 72 }} />}
        </div>
      </div>

      {months?.length > 1 && (
        <div className="card" style={{ padding: 12, marginBottom: 16, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {months.map((m) => (
            <button key={m.id} className={`btn btn-sm ${m.report_month === month ? 'btn-primary' : 'btn-outline'}`} onClick={() => switchMonth(m.report_month)}>
              {monthLabel(m.report_month)}
            </button>
          ))}
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title" style={{ marginBottom: 12 }}>المشاريع المموَّلة</div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>المشروع</th><th>إجمالي التمويل</th><th>المستهلَك</th><th>المتبقّي</th><th>حالة السداد</th><th></th></tr>
            </thead>
            <tbody>
              {summary.funded_projects.map((p) => (
                <tr key={p.id}>
                  <td style={{ fontWeight: 600 }}>
                    <Link to={projectLink(p.id)}>{p.name}</Link>
                    {p.unit_basis && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.unit_basis}</div>}
                  </td>
                  <td>{fmtMoney(p.funding_total)}</td>
                  <td>{fmtMoney(p.total_used)}</td>
                  <td>{fmtMoney(p.total_remaining)}</td>
                  <td><PaymentBadge status={p.payment_status} /></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <Link className="btn btn-sm btn-outline" to={projectLink(p.id)}>المالي ←</Link>{' '}
                    {p.technical_url && <a className="btn btn-sm btn-outline" href={p.technical_url} target="_blank" rel="noreferrer">الفني ↗</a>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, background: 'var(--surface-alt)' }}>
                <td>الإجمالي</td>
                <td>{fmtMoney(summary.totals.funding_total)}</td>
                <td>{fmtMoney(summary.totals.total_used)}</td>
                <td>{fmtMoney(summary.totals.total_remaining)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {summary.pending_projects.length > 0 && (
        <div className="card" style={{ borderInlineStart: '4px solid var(--info)' }}>
          <div className="card-title" style={{ marginBottom: 8 }}>مبالغ معتمدة بانتظار التمويل</div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>المشروع</th><th>المبلغ المعتمد</th><th></th></tr></thead>
              <tbody>
                {summary.pending_projects.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{p.name}</td>
                    <td>{fmtMoney(p.approved_amount)}</td>
                    <td>{p.technical_url && <a className="btn btn-sm btn-outline" href={p.technical_url} target="_blank" rel="noreferrer">الفني ↗</a>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const wrap = { maxWidth: 1000, margin: '0 auto', padding: 20 };
